import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError, finalize } from 'rxjs/operators';

export interface User {
  id: string;      // mapped from sub
  sub?: string;
  username: string;
  email: string;
  role: string;
  priviledge?: number;
  permissions?: number | string[];  // legacy number OR node-auth string[]
  roles?: any[];  // can be string[] (node-auth) or object[] with .id/.name (legacy)
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = '/api/auth';

  // Signal for user state
  private _currentUser = signal<User | null>(null);
  public readonly currentUser = this._currentUser.asReadonly();

  // Tracks whether the first session check has completed (avoids premature guard redirects)
  private _initialized = signal<boolean>(false);

  // Maintain BehaviorSubject for compatibility with existing components
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) { }

  register(username: string, email: string, password: string, confirmPassword: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, {
      username,
      email,
      password,
      confirmPassword,
    }, { withCredentials: true }).pipe(
      tap((response: any) => {
        // Il token viene salvato automaticamente nel cookie dal browser. Aggiorniamo solo l'utente.
        if (response.user) {
          this.updateUserState(response.user);
        }
      }),
    );
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, { email, password }, { withCredentials: true }).pipe(
      tap((response: any) => {
        // node-auth login returns {success:true} with cookies; will fetch user via /me next
        if (response.user) {
          this.updateUserState(response.user);
        }
      }),
    );
  }

  logout(): Observable<any> {
    console.log('[LOGOUT] Starting logout...');
    return this.http.post(`${this.apiUrl}/logout`, {}, { withCredentials: true }).pipe(
      tap({
        next: () => {
          console.log('[LOGOUT] Server response received');
          this.performClientLogout();
        },
        error: (err) => {
          console.error('[LOGOUT] Server error during logout:', err);
          this.performClientLogout();
        }
      })
    );
  }

  public deleteEverythingAndGoToLogin(): void {
    this.performClientLogout();
    // Non possiamo iniettare Router qui per via degli interceptor/circular dep spesso,
    // ma AuthService solitamente è safe se iniettato correttamente.
    // In questo progetto dashboard.component e login.component gestiscono la navigazione dopo la sottoscrizione.
    // Forziamo comunque l'evento di logout per chiunque ascolti.
  }

  private performClientLogout(): void {
    this.clearCookies();
    this.updateUserState(null); // Resetta lo stato interno dell'utente loggato
    console.log('[LOGOUT] Client-side state cleared');
  }

  refreshToken(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/refresh`, {}, { withCredentials: true });
  }

  /** Called by the guard on first load to check if there's a live session. Always completes, never errors. */
  checkSession(): Observable<User | null> {
    return this.http.get<any>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      map((res: any) => {
        const rawUser = res?.user ?? res;
        if (rawUser?.sub || rawUser?.email) {
          const user = this.mapUser(rawUser);
          this.updateUserState(user);
          return user;
        }
        return null;
      }),
      catchError(() => {
        // 401/403 or refresh-retry failure — user is not authenticated
        this.updateUserState(null);
        return of(null); // Always complete with null, never propagate errors
      }),
      finalize(() => {
        this._initialized.set(true); // Mark session check complete regardless of outcome
      })
    );
  }

  getCurrentUser(): Observable<any> {
    return this.http.get(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      tap((res: any) => {
        const rawUser = res?.user ?? res;
        if (rawUser?.sub || rawUser?.email) {
          this.updateUserState(this.mapUser(rawUser));
        }
      })
    );
  }

  private mapUser(rawUser: any): User {
    return {
      id: rawUser.sub ?? rawUser.id,
      sub: rawUser.sub,
      username: rawUser.firstName || rawUser.username || rawUser.email?.split('@')[0] || '',
      email: rawUser.email,
      role: rawUser.role,
      priviledge: rawUser.priviledge ?? 0,
      permissions: rawUser.permissions,
      roles: rawUser.roles,
    };
  }

  isInitialized(): boolean {
    return this._initialized();
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/change-password`, { oldPassword, newPassword });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, { token, newPassword });
  }

  // Verifica basica: controlliamo se esiste un cookie accessToken (fallback SSR safe)
  // NOTA: Con HttpOnly: true impostato sul server, il frontend ESATTO non potra leggere il cookie via document.cookie.
  // Pertanto l'autenticazione lato client diventerà uno state pattern puro basato su currentUser().
  getAccessToken(): string | null {
    // In un'architettura Strict HTTP-Only, disabilitiamo la lettura locale. Ritorna null.
    // L'autenticità è decisa backend-side tramite richiesta.
    return null;
  }

  private clearCookies(): void {
    // Il backend si occuperà di ripulire i cookie `HttpOnly` tramite rotta /logout.
    // Manteniamo qui solo una best-effort purge in caso rimanessero ghost non-Secure.
    if (typeof document === 'undefined') return;
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  private updateUserState(user: User | null = null): void {
    this._currentUser.set(user);
    this.currentUserSubject.next(user);
  }

  isAuthenticated(): boolean {
    // In Strict HTTP-Only, l'autenticazione è determinata dal caricamento del profilo
    return this._currentUser() !== null;
  }
}
